import React, { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * Lightweight HTML5 signature pad — touch + mouse.
 * Exposes `.toBlob(cb)` via `ref` if needed.
 */
export default function SignaturePad({ onChange, height = 200 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = height * ratio;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
    ctx.lineWidth = 2.2;
    // White background so the resulting PNG is print-ready
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, height);
  }, [height]);

  const point = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setEmpty(false);
  };
  const end = () => {
    drawing.current = false;
    if (onChange) {
      canvasRef.current.toBlob((b) => onChange(b), "image/png");
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, height);
    setEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="border border-[var(--border-strong)] bg-white rounded-sm overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="w-full block touch-none cursor-crosshair"
          style={{ height }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[var(--text-3)]">{empty ? "Bitte hier unterschreiben" : "Unterschrift erfasst"}</span>
        <button
          type="button"
          onClick={clear}
          data-testid="signature-clear"
          className="text-xs uppercase tracking-wider text-[var(--text-2)] hover:text-[var(--text)] flex items-center gap-1"
        >
          <Eraser size={14} strokeWidth={1.5} /> Löschen
        </button>
      </div>
    </div>
  );
}
